<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import MetricList from '../parts/MetricList.vue';
import { formatCount, formatDateTime } from '../helpers';

const props = defineProps({
  source: { type: Object, required: true },
});

const { t, locale } = useI18n();

const mention = computed(() => ({
  ...(props.source.post || {}),
  ...(props.source.mention || {}),
}));

const metrics = computed(() => [
  {
    key: 'views',
    icon: 'i-lucide-eye',
    value: mention.value.views,
    label: t('MULTICHAT.SOURCE.METRICS.VIEWS'),
  },
  {
    key: 'likes',
    icon: 'i-lucide-heart',
    value: mention.value.likes,
    label: t('MULTICHAT.SOURCE.METRICS.LIKES'),
  },
  {
    key: 'comments',
    icon: 'i-lucide-message-circle',
    value: mention.value.comments,
    label: t('MULTICHAT.SOURCE.METRICS.COMMENTS'),
  },
]);

const initials = computed(() =>
  (mention.value.author || '?').replace('@', '').slice(0, 1).toUpperCase()
);

const url = computed(() => mention.value.url || props.source.url);
</script>

<template>
  <div
    class="flex flex-col gap-2.5 p-3 rounded-lg bg-n-iris-2 border border-n-iris-4"
  >
    <div class="flex items-center gap-2.5 min-w-0">
      <span
        class="grid flex-shrink-0 size-9 rounded-full place-items-center bg-n-iris-9 text-white text-sm font-bold"
      >
        {{ initials }}
      </span>
      <div class="flex flex-col min-w-0">
        <div class="flex items-center gap-1.5 min-w-0">
          <a
            v-if="mention.author_url"
            :href="mention.author_url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-[13px] font-semibold text-n-slate-12 hover:underline truncate"
          >
            {{ mention.author }}
          </a>
          <span
            v-else
            class="text-[13px] font-semibold text-n-slate-12 truncate"
          >
            {{ mention.author }}
          </span>
          <span
            v-if="mention.followers"
            class="inline-flex items-center gap-0.5 h-5 px-1.5 rounded-md bg-n-alpha-2 text-[11px] font-medium text-n-slate-11 flex-shrink-0"
          >
            <span class="i-lucide-users size-3" />
            {{
              t('MULTICHAT.SOURCE.FOLLOWERS', {
                count: formatCount(mention.followers, locale),
              })
            }}
          </span>
        </div>
        <span class="text-[11px] text-n-iris-11">
          <span class="i-lucide-at-sign size-3 align-[-2px]" />
          {{ t('MULTICHAT.SOURCE.MENTION.TAGGED') }}
          <template v-if="mention.published_at">
            · {{ formatDateTime(mention.published_at, locale) }}
          </template>
        </span>
      </div>
    </div>
    <p
      v-if="mention.text"
      class="m-0 text-[13px] leading-5 text-n-slate-12 whitespace-pre-line line-clamp-4"
    >
      {{ mention.text }}
    </p>
    <div class="flex flex-wrap items-center gap-2">
      <MetricList :items="metrics" />
      <span class="flex-1" />
      <a
        v-if="url"
        :href="url"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1 text-xs font-medium text-n-blue-11 hover:underline"
      >
        {{ t('MULTICHAT.SOURCE.OPEN_POST') }}
        <span class="i-lucide-external-link size-3" />
      </a>
    </div>
  </div>
</template>
